/*
 * Pure lane logic for the admin RD registry (NO-650). No React and relative
 * imports only, so it loads under `tsx --test`. An RD is Linked only when both
 * its sign-in account and its Healthie record are linked; either gap puts it
 * in Needs a link, because either gap is fixed on NDD Admin's Provider Links.
 */

export const NDD_ADMIN_PROVIDER_LINKS_URL = "https://admin.nodietdietitian.com/staff";

export interface RegistrySignInAccount {
  userId: string;
  name: string;
  email: string;
}

export interface RegistryLinkFacts {
  supervisorId: string | null;
  signInAccount: RegistrySignInAccount | null;
  healthieRecordUrl: string | null;
}

export type RegistryLane = "needs-link" | "linked";

export const REGISTRY_LANE_ORDER: readonly RegistryLane[] = ["needs-link", "linked"];

export const REGISTRY_LANE_LABELS: Record<RegistryLane, string> = {
  "needs-link": "Needs a link",
  linked: "Linked",
};

export interface RegistryLaneGroup<Row extends RegistryLinkFacts> {
  lane: RegistryLane;
  label: string;
  rows: Row[];
}

export interface RegistryRollup {
  total: number;
  needLink: number;
  noSupervisor: number;
  linked: number;
}

export function isRegistryRowLinked(row: RegistryLinkFacts): boolean {
  return Boolean(row.signInAccount) && Boolean(row.healthieRecordUrl);
}

export function registryLaneFor(row: RegistryLinkFacts): RegistryLane {
  return isRegistryRowLinked(row) ? "linked" : "needs-link";
}

/** Server order is kept within each lane; a lane with no rows is omitted. */
export function groupRegistryIntoLanes<Row extends RegistryLinkFacts>(
  rows: Row[]
): RegistryLaneGroup<Row>[] {
  return REGISTRY_LANE_ORDER.map((lane) => ({
    lane,
    label: REGISTRY_LANE_LABELS[lane],
    rows: rows.filter((row) => registryLaneFor(row) === lane),
  })).filter((group) => group.rows.length > 0);
}

export function registryRollup(rows: RegistryLinkFacts[]): RegistryRollup {
  const linked = rows.filter(isRegistryRowLinked).length;

  return {
    total: rows.length,
    needLink: rows.length - linked,
    noSupervisor: rows.filter((row) => !row.supervisorId).length,
    linked,
  };
}

function comparableName(name: string): string {
  return name.trim().replace(/\s+/g, " ").toLowerCase();
}

/** The linked user's name is shown only when it tells the admin something new. */
export function signInNameDiffers(accountName: string, registryName: string): boolean {
  return comparableName(accountName) !== comparableName(registryName);
}
